import { Box, Checkbox, Chip, Divider, MenuItem, Typography } from "@mui/material"
import { AddOutlined, CloseOutlined, DeleteOutlined } from "@mui-symbols-material/w300"
import React, { useState } from "react"
import { type Control, Controller, type FieldArrayWithId } from "react-hook-form"

import { Button } from "@/components/common/Button"
import MultiSelect from "@/components/MultiSelect/MultiSelect"
import { ContactRole, type Location } from "@/graphql/codegen/graphql"
import type { ContactLocationEditFormValues } from "@/schemas/contactFormSchema"
import { formatLocationAddress } from "@/utils/utils"

export interface EditContactLocationRolesFormFieldsProps {
  fieldArrayName: string
  fields: FieldArrayWithId[]
  control: Control<any>
  locations: Location[]
  locationRoles: ContactLocationEditFormValues[]
  onLocationRemove: (index: number) => void
  onRoleRemove: (index: number, role: string) => void
}

export const EditContactLocationRolesFormFields = ({
  fieldArrayName,
  fields,
  control,
  locations,
  locationRoles,
  onLocationRemove,
  onRoleRemove,
}: EditContactLocationRolesFormFieldsProps): React.ReactNode => {
  const [editIndexes, setEditIndexes] = useState<number[]>([])
  const roleOptions = Object.keys(ContactRole).map((key) => {
    const value = ContactRole[key as keyof typeof ContactRole]
    return { value: value, label: value }
  })

  const getLocationDisplayValue = (locationId: string) =>
    formatLocationAddress(locations.find((location) => location.locationId === locationId))

  return (
    <Box>
      {fields.map((field, index) => (
        <Box key={field.id} className='flex flex-col gap-2.5'>
          <Box className='flex w-full items-center justify-between'>
            <Typography variant='body2'>{getLocationDisplayValue(locationRoles[index].locationId)}</Typography>
            <Button
              variant='primary'
              appearance='text'
              className='-my-1'
              startIcon={<DeleteOutlined />}
              onClick={() => onLocationRemove(index)}
            >
              Remove
            </Button>
          </Box>
          <Box className='flex flex-wrap gap-2.5'>
            {locationRoles[index]?.currentRoles.map((role) => (
              <Chip
                key={role}
                label={role}
                className='h-6 rounded-sm bg-gray-100 font-[12px] text-gray-900'
                onDelete={() => onRoleRemove(index, role)}
                deleteIcon={<CloseOutlined className='!h-4 !w-4 text-tertiary-yellow-900' />}
              />
            ))}
            <Button
              variant='primary'
              appearance='text'
              size='small'
              className='h-6 px-2 py-0'
              startIcon={<AddOutlined />}
              onClick={() => setEditIndexes((editIndexes) => [...editIndexes, index])}
            >
              Add Role(s)
            </Button>
          </Box>
          {editIndexes.includes(index) && (
            <Controller
              name={`${fieldArrayName}.${index}.newRoles`}
              control={control}
              render={({ field }) => (
                <MultiSelect
                  id={`${fieldArrayName}.${index}.newRoles`}
                  value={field.value}
                  onChange={(values) => field.onChange(values)}
                  options={roleOptions}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 300 },
                    },
                  }}
                  children={Object.keys(ContactRole).map((key) => {
                    const value = ContactRole[key as keyof typeof ContactRole]
                    const isChecked = field.value?.includes(value)
                    return (
                      <MenuItem key={key} value={value} disabled={locationRoles[index].currentRoles.includes(value)}>
                        <Checkbox checked={!!isChecked} />
                        {value}
                      </MenuItem>
                    )
                  })}
                />
              )}
            />
          )}
          {index !== fields.length - 1 && <Divider className='my-4' />}
        </Box>
      ))}
    </Box>
  )
}
